import errorResolver from "../errorResolver";
import { usePromisedRequest as promiseRequest } from "../../Common/request";
import { resolveNoContent, resolveWithBody } from "../Utils/utils";
import { BracketType, Category } from "../../Common/types";
import { GenderType } from "../../Common/Competitors/types";

export type DisciplineType =
    | "bjj"
    | "submissionFighting"
    | "mma"
    | "combatJuJutsu"
    | "wrestling"
    | "grappling"
    | "judo"
    | "kata"
    | "aikido"
    | "sambo"
    | "kickboxing"
    | "k1"
    | "boxing"
    | "muayThai"
    | "taekwondo"
    | "karate"
    | "juJitsu"
    | "fighting"
    | "newaza";

export type OptionalParams = {
    fightTime?: number | null;
    discipline?: DisciplineType | null;
    ageMin?: number | null;
    ageMax?: number | null;
    gender?: GenderType | null;
};

export type CategoryKeyValue = {
    key: number;
    value: string;
};

export type Parameter = {
    id: number;
    name: string;
    eventId: number;
    ageMin: number | null;
    ageMax: number | null;
    gender: GenderType | null;
    scoreboard: string | null;
    discipline: DisciplineType | null;
    fightTime: number | null;
    order: number | null;
};

export type CategoryFightTime = {
    time: string;
    time_in_seconds: number;
    inherited: boolean;
};

export type CategoryDiscipline = {
    name: string | null;
    inherited: boolean;
};

export type CategoryGender = {
    name: GenderType | null;
    inherited: boolean;
};

export type CategoryAgeLimit = {
    ageMin: number | null;
    ageMax: number | null;
    inherited: boolean;
};

export type CategoryNode = {
    id: number;
    name: string;
    fightTime: CategoryFightTime;
    disciplineType: CategoryDiscipline;
    categoryGender: CategoryGender;
    categoryAgeLimit: CategoryAgeLimit;
    parentId: number | null;
    extendingNode?: boolean;
};

export type CategoryInheritableValue<T> = {
    value: T | null;
    inherited: boolean;
    ancestorValue: T | null;
};

export type CategoryMetadataResponse = {
    id: number;
    name: string;
    fightTime: CategoryInheritableValue<number>;
    discipline: CategoryInheritableValue<DisciplineType>;
    gender: CategoryInheritableValue<GenderType>;
    ageMin: CategoryInheritableValue<number>;
    ageMax: CategoryInheritableValue<number>;
};

export type ParameterLeafStatus = {
    id: number;
    name: string;
    totalRegistrations: number;
};

export type CategoryParameterAddition = { eventId: number; parentId: number; name: string };

export type CategoriesOptions = {
    useBirthDateForCategoryMatching: boolean;
};

export type CategorySidebarSummary = {
    parameterId: number;
    name: string;
    hasBracket: boolean;
    bracketsShared: boolean;
    competitorsCount: number;
    bracketType: BracketType | null;
    hasVisibleSchedule: boolean;
};

export default {
    importCategories(sourceEventId: number, targetEventId: number, branchName?: string) {
        return promiseRequest({
            url: `/api/admin/events/${targetEventId}/categories-import`,
            method: "PUT",
            json: {
                sourceEventId: sourceEventId,
                targetEventId: targetEventId,
                ...(branchName ? { branchName } : {}),
            },
        }).then(res => {
            errorResolver.throwSimpleError(res);

            if (res.statusCode !== 204) {
                throw Error("Could not import categories.");
            }
        });
    },
    getCategoryMetadata(eventId: number, id: number): Promise<CategoryMetadataResponse> {
        return promiseRequest<CategoryMetadataResponse>({
            url: `/api/admin/events/${eventId}/categories/${id}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getCategories(eventId: number, options: { hideEmpty?: boolean } = {}): Promise<Category[]> {
        return promiseRequest<Category[]>({
            url: `/api/admin/events/${eventId}/categories${options.hideEmpty ? "?hideEmpty=1" : ""}`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    getCategoriesWithMetadata(eventId: number): Promise<Parameter[]> {
        return promiseRequest<Parameter[]>({
            url: `/api/events/${eventId}/categories/metadata`,
            json: true,
            method: "get",
        }).then(resolveWithBody("Could not fetch categories metadata list for event " + eventId));
    },
    getCategoriesFlatTree(eventId: number): Promise<Record<number, CategoryNode[]>> {
        return promiseRequest<Record<number, CategoryNode[]>>({
            url: `/api/admin/events/${eventId}/categories-flat-tree`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode === 404 && errorResolver.getErrorDetails(res) === "NO_ROOT") {
                return {};
            }

            if (res.statusCode !== 200) {
                throw Error("Could not fetch category flat tree for event " + eventId);
            }

            return res.body;
        });
    },
    createRoot(eventId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories`,
            method: "put",
            json: true,
        }).then(resolveNoContent());
    },
    addParameter(payload: CategoryParameterAddition): Promise<number> {
        return promiseRequest<number>({
            url: `/api/admin/events/${payload.eventId}/categories/${payload.parentId}`,
            method: "post",
            json: payload,
        }).then(resolveWithBody());
    },
    updateParameter(eventId: number, parameterId: number, newName: string, optionalParams: OptionalParams = {}) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}`,
            method: "put",
            json: {
                eventId: eventId,
                id: parameterId,
                name: newName,
                fightTime: optionalParams.fightTime,
                discipline: optionalParams.discipline,
                ageMin: optionalParams.ageMin,
                ageMax: optionalParams.ageMax,
                gender: optionalParams.gender,
            },
        }).then(res => {
            errorResolver.throwValidationErrorsAsText(res);

            if (res.statusCode !== 204) {
                throw Error("Could not delete parameter.");
            }
        });
    },
    moveParameterUnderNewParent(eventId: number, parameterId: number, newParentId: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/parents`,
            json: {
                eventId: eventId,
                parameterId: parameterId,
                newParentId: newParentId,
            },
            method: "put",
        }).then(resolveNoContent());
    },
    duplicateParameterUnderSameParent(eventId: number, parameterId: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/duplicates`,
            json: {
                eventId: eventId,
                parameterId: parameterId,
            },
            method: "post",
        }).then(resolveNoContent());
    },
    updateOrderOfParameter(eventId: number, id: number, oldIndex: number, newIndex: number) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${id}/orderings`,
            json: {
                id: id,
                oldIndex: oldIndex,
                newIndex: newIndex,
            },
            method: "put",
        }).then(res => {
            if (res.statusCode !== 204) {
                throw Error("Could not move parameter.");
            }
        });
    },
    clearParams(eventId: number, parameterId: number, paramsToClear: string[]) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/properties`,
            method: "delete",
            json: {
                paramsToClear: paramsToClear,
            },
        }).then(res => {
            if (res.statusCode !== 204) {
                throw Error("Could not clear fight time for parameter.");
            }
        });
    },
    getTreeStatusForParameter(eventId: number, parameterId: number): Promise<ParameterLeafStatus[]> {
        return promiseRequest<ParameterLeafStatus[]>({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/trees`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    deleteParameterTree(eventId: number, parameterId: number): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/${parameterId}/trees`,
            json: true,
            method: "delete",
        }).then(resolveNoContent());
    },
    getAllLevels(eventId: number): Promise<CategoryKeyValue[]> {
        return promiseRequest<CategoryKeyValue[]>({
            url: `/api/events/${eventId}/categories-all-levels`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    getCategoriesOptions(eventId: number): Promise<CategoriesOptions> {
        return promiseRequest<CategoriesOptions>({
            url: `/api/admin/events/${eventId}/categories/options`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    saveCategoriesOptions(eventId: number, options: CategoriesOptions): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/categories/actions/save-options`,
            method: "put",
            json: options,
        }).then(resolveNoContent());
    },
    getCategorySidebarSummary(eventId: number, parameterId: number): Promise<CategorySidebarSummary> {
        return promiseRequest<CategorySidebarSummary>({
            url: `/api/public/events/${eventId}/categories/${parameterId}/sidebar`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
